import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from './../ui/input';
import { Checkbox } from './../ui/checkbox';
import { Badge } from './../ui/badge';
import { Plus, Trash2, Clock } from 'lucide-react';

export default function SubtaskList({ subtasks = [], onChange, isEditing }) {
  const [newSubtask, setNewSubtask] = useState({ title: '', estimated_hours: '' });

  const handleToggle = (id) => {
    const updated = subtasks.map(st =>
      st.id === id ? { ...st, completed: !st.completed } : st
    );
    onChange(updated);
  };

  const handleAdd = () => {
    if (!newSubtask.title.trim()) return;
    
    const updated = [
      ...subtasks,
      {
        id: Date.now().toString(),
        title: newSubtask.title,
        completed: false,
        estimated_hours: newSubtask.estimated_hours ? parseFloat(newSubtask.estimated_hours) : undefined
      }
    ];
    onChange(updated);
    setNewSubtask({ title: '', estimated_hours: '' });
  };

  const handleDelete = (id) => {
    onChange(subtasks.filter(st => st.id !== id));
  };

  const completedCount = subtasks.filter(st => st.completed).length;
  const totalCount = subtasks.length;

  return (
    <div className="space-y-3">
      {/* Progress Header */}
      {totalCount > 0 && (
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-medium text-slate-600">
            Progresso: {completedCount}/{totalCount}
          </span>
          <div className="flex-1 mx-3 h-2 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all duration-300"
              style={{ width: `${totalCount > 0 ? (completedCount / totalCount) * 100 : 0}%` }}
            />
          </div>
          <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 font-semibold">
            {Math.round((completedCount / totalCount) * 100)}%
          </Badge>
        </div>
      )}

      {/* Subtask List */}
      <div className="space-y-2">
        {subtasks.map((subtask) => (
          <div
            key={subtask.id}
            className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200 group hover:border-blue-300 transition-all"
          >
            <Checkbox
              checked={subtask.completed}
              onCheckedChange={() => handleToggle(subtask.id)}
              className="shrink-0"
            />
            <div className="flex-1 min-w-0">
              <span className={`text-sm ${subtask.completed ? 'line-through text-slate-400' : 'text-slate-700'}`}>
                {subtask.title}
              </span>
              {subtask.estimated_hours && (
                <div className="flex items-center gap-1 text-xs text-slate-500 mt-1">
                  <Clock className="w-3 h-3" />
                  {subtask.estimated_hours}h
                </div>
              )}
            </div>
            {isEditing && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700"
                onClick={() => handleDelete(subtask.id)}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        ))}
      </div>

      {/* Add New Subtask */}
      {isEditing && (
        <div className="flex gap-2 pt-2">
          <Input
            value={newSubtask.title}
            onChange={(e) => setNewSubtask({ ...newSubtask, title: e.target.value })}
            placeholder="Nova subtarefa..."
            className="flex-1"
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          />
          <Input
            type="number"
            value={newSubtask.estimated_hours}
            onChange={(e) => setNewSubtask({ ...newSubtask, estimated_hours: e.target.value })}
            placeholder="Horas"
            className="w-20"
            min="0"
            step="0.5"
          />
          <Button
            size="sm"
            onClick={handleAdd}
            className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 shrink-0"
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}